/**
 * OpenFreeMap Dark, patched for a radar-scope look:
 * almost-black land, navy water, thin muted roads, no highway shields.
 */

export const OPENFREEMAP_DARK_STYLE_URL = "https://tiles.openfreemap.org/styles/dark";

export const OPENFREEMAP_ATTRIBUTION =
  '<a href="https://openfreemap.org" target="_blank" rel="noopener">OpenFreeMap</a> ' +
  '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors';

export const BASEMAP_COLORS = {
  background: "#05070c",
  land: "#0a0e17",
  water: "#121c2e",
  waterway: "#0e1828",
  road: "#3a4a58",
  roadMajor: "#4a6270",
  roadMotorway: "#3d5c68",
  roadCasing: "#1a2430",
  label: "#9aa8b5",
  labelHalo: "#05070c",
  boundary: "#2a3544",
  // A step above country borders so state lines read on near-black land.
  boundaryState: "#4a5d72",
};

/** Road names, shields, one-ways, rails, aeroways — clutter on radar. */
export const ALWAYS_HIDDEN_LAYER_IDS = Object.freeze([
  "highway_name_motorway",
  "highway_name_other",
  "road_oneway",
  "road_oneway_opposite",
  "railway_transit",
  "railway_transit_dashline",
  "railway_minor",
  "railway_minor_dashline",
  "railway",
  "railway_dashline",
  "aeroway-taxiway",
  "aeroway-runway-casing",
  "aeroway-area",
  "aeroway-runway",
]);

/** Shown only when Advanced → Extra map labels is on. */
export const EXTRA_LABEL_LAYER_IDS = Object.freeze([
  "place_other",
  "place_suburb",
  "place_village",
  "place_town",
  "water_name",
]);

const ALWAYS_HIDDEN = new Set(ALWAYS_HIDDEN_LAYER_IDS);
const EXTRA_LABELS = new Set(EXTRA_LABEL_LAYER_IDS);

const LABEL_PAINT = {
  "text-color": BASEMAP_COLORS.label,
  "text-halo-color": BASEMAP_COLORS.labelHalo,
  "text-halo-width": 1.2,
  "text-halo-blur": 0.4,
};

const LAYER_PAINT = {
  background: { "background-color": BASEMAP_COLORS.background },
  water: { "fill-color": BASEMAP_COLORS.water, "fill-antialias": true },
  waterway: { "line-color": BASEMAP_COLORS.waterway, "line-opacity": 0.85 },
  landcover_ice_shelf: { "fill-color": BASEMAP_COLORS.land },
  landcover_glacier: { "fill-color": BASEMAP_COLORS.land },
  landuse_residential: { "fill-color": BASEMAP_COLORS.land, "fill-opacity": 0.25 },
  landcover_wood: { "fill-color": "#0c1210", "fill-opacity": 0.3 },
  landuse_park: { "fill-color": "#0c1210" },
  building: {
    "fill-color": "#080c12",
    "fill-outline-color": "#101820",
    "fill-opacity": 0.7,
  },
  road_area_pier: { "fill-color": BASEMAP_COLORS.land },
  road_pier: { "line-color": BASEMAP_COLORS.road },
  highway_path: {
    "line-color": BASEMAP_COLORS.road,
    "line-opacity": 0.3,
    "line-width": ["interpolate", ["exponential", 1.2], ["zoom"], 14, 0.3, 20, 1.6],
  },
  highway_minor: {
    "line-color": BASEMAP_COLORS.road,
    "line-opacity": 0.4,
    "line-width": ["interpolate", ["exponential", 1.4], ["zoom"], 12, 0.35, 20, 3.2],
  },
  highway_major_casing: {
    "line-color": BASEMAP_COLORS.roadCasing,
    "line-opacity": 0.45,
    "line-width": ["interpolate", ["exponential", 1.3], ["zoom"], 10, 1.1, 20, 6.5],
  },
  highway_major_inner: {
    "line-color": BASEMAP_COLORS.roadMajor,
    "line-opacity": 0.7,
    "line-width": ["interpolate", ["exponential", 1.3], ["zoom"], 10, 0.7, 20, 5],
  },
  highway_major_subtle: {
    "line-color": BASEMAP_COLORS.roadMajor,
    "line-opacity": 0.5,
    "line-width": ["interpolate", ["linear"], ["zoom"], 6, 0.25, 11, 1],
  },
  highway_motorway_casing: {
    "line-color": BASEMAP_COLORS.roadCasing,
    "line-opacity": 0.5,
    "line-width": ["interpolate", ["exponential", 1.4], ["zoom"], 6, 1, 20, 8],
  },
  highway_motorway_inner: {
    "line-color": BASEMAP_COLORS.roadMotorway,
    "line-opacity": 0.75,
    "line-width": ["interpolate", ["exponential", 1.4], ["zoom"], 5, 0.55, 20, 5.5],
  },
  highway_motorway_subtle: {
    "line-color": BASEMAP_COLORS.roadMotorway,
    "line-opacity": 0.55,
    "line-width": ["interpolate", ["exponential", 1.4], ["zoom"], 4, 0.5, 6, 0.9],
  },
  boundary_state: {
    "line-color": BASEMAP_COLORS.boundaryState,
    "line-opacity": 0.85,
    "line-width": ["interpolate", ["exponential", 1.2], ["zoom"], 3, 0.55, 12, 1.5],
  },
  "boundary_country_z0-4": {
    "line-color": BASEMAP_COLORS.boundary,
    "line-opacity": 0.7,
    "line-width": ["interpolate", ["exponential", 1.1], ["zoom"], 3, 0.55, 8, 1.4],
  },
  "boundary_country_z5-": {
    "line-color": BASEMAP_COLORS.boundary,
    "line-opacity": 0.7,
    "line-width": ["interpolate", ["exponential", 1.1], ["zoom"], 5, 0.7, 12, 1.8],
  },
};

const LABEL_SIZE = {
  place_city: 10,
  place_city_large: 12,
  place_state: 10,
  place_country_other: 10,
  place_country_minor: 11,
  place_country_major: 13,
  place_town: 9,
  place_village: 9,
  place_suburb: 9,
  place_other: 8,
};

export function isLayerVisible(layerId, extraLabels = false) {
  if (ALWAYS_HIDDEN.has(layerId)) return false;
  if (EXTRA_LABELS.has(layerId)) return !!extraLabels;
  return true;
}

export function extraLabelVisibility(extraLabels) {
  return extraLabels ? "visible" : "none";
}

function cloneStyle(style) {
  return JSON.parse(JSON.stringify(style));
}

function mergePaint(layer, overrides) {
  layer.paint = { ...(layer.paint || {}), ...overrides };
}

function ensureLayout(layer) {
  layer.layout = { ...(layer.layout || {}) };
  return layer.layout;
}

/**
 * Clone an OpenFreeMap (or compatible) style and retint / filter it
 * for RadarRewind. Does not mutate the input.
 */
export function patchBasemapStyle(style, options = {}) {
  const extraLabels = !!options.extraLabels;
  const patched = cloneStyle(style);

  if (patched.sources && typeof patched.sources === "object") {
    for (const source of Object.values(patched.sources)) {
      if (source && typeof source === "object" && !source.attribution) {
        source.attribution = OPENFREEMAP_ATTRIBUTION;
      }
    }
  }

  const layers = Array.isArray(patched.layers) ? patched.layers : [];
  for (const layer of layers) {
    if (!layer || typeof layer.id !== "string") continue;

    const paintOverrides = LAYER_PAINT[layer.id];
    if (paintOverrides) {
      mergePaint(layer, paintOverrides);
    }

    if (layer.id === "landcover_wood" && layer.paint) {
      delete layer.paint["fill-pattern"];
    }

    if (layer.type === "symbol") {
      mergePaint(layer, LABEL_PAINT);
      const layout = ensureLayout(layer);
      if (LABEL_SIZE[layer.id] != null) {
        layout["text-size"] = LABEL_SIZE[layer.id];
      }
      delete layout["icon-image"];
    }

    ensureLayout(layer).visibility = isLayerVisible(layer.id, extraLabels)
      ? "visible"
      : "none";
  }

  return patched;
}

export const FALLBACK_BASEMAP_STYLE = {
  version: 8,
  name: "RadarRewind fallback",
  sources: {},
  layers: [
    {
      id: "background",
      type: "background",
      paint: { "background-color": BASEMAP_COLORS.background },
    },
  ],
};

// Light, thin lines drawn over GOES imagery, where the basemap's own borders
// are hidden. Cloud tops are bright, so the lines stay faint.
export const SATELLITE_BORDER_PAINT = Object.freeze({
  boundary_state: {
    "line-color": "#e6edf5",
    "line-opacity": 0.4,
    "line-width": ["interpolate", ["exponential", 1.2], ["zoom"], 3, 0.5, 12, 1.2],
  },
  "boundary_country_z0-4": {
    "line-color": "#e6edf5",
    "line-opacity": 0.55,
    "line-width": ["interpolate", ["exponential", 1.1], ["zoom"], 3, 0.6, 8, 1.2],
  },
  "boundary_country_z5-": {
    "line-color": "#e6edf5",
    "line-opacity": 0.55,
    "line-width": ["interpolate", ["exponential", 1.1], ["zoom"], 5, 0.7, 12, 1.5],
  },
});

// The ocean's edge, from the water polygons. The tiles carry no coastline
// lines, and the boundary layer's offshore (maritime) lines only appear at
// some zooms, miles out to sea.
export const SATELLITE_COAST_PAINT = Object.freeze({
  "line-color": "#e6edf5",
  "line-opacity": 0.5,
  "line-width": ["interpolate", ["exponential", 1.1], ["zoom"], 3, 0.6, 12, 1.4],
});

const LEGACY_COMPARE = new Set(["==", "!=", "<", "<=", ">", ">=", "in", "!in", "has", "!has"]);

// Legacy filters (["==", "admin_level", 4]) and expressions can't be mixed.
function isLegacyFilter(filter) {
  if (!Array.isArray(filter)) return false;
  const [op, ...args] = filter;
  if (op === "none") return true;
  if (op === "all" || op === "any") return args.some(isLegacyFilter);
  return LEGACY_COMPARE.has(op) && typeof args[0] === "string";
}

function withoutMaritime(filter) {
  if (!filter) return ["!=", ["get", "maritime"], 1];
  return isLegacyFilter(filter)
    ? ["all", filter, ["!=", "maritime", 1]]
    : ["all", filter, ["!=", ["get", "maritime"], 1]];
}

/**
 * The ocean's edge and the land state and country borders from an
 * OpenFreeMap style, on a transparent map, for the pane above the satellite
 * layer.
 */
export function satelliteBorderStyle(style) {
  const source = cloneStyle(style);
  const all = Array.isArray(source.layers) ? source.layers : [];
  const borders = all
    .filter((layer) => layer && SATELLITE_BORDER_PAINT[layer.id])
    .map((layer) => ({
      ...layer,
      filter: withoutMaritime(layer.filter),
      paint: { ...SATELLITE_BORDER_PAINT[layer.id] },
      layout: { ...(layer.layout || {}), visibility: "visible" },
    }));
  const water = all.find((layer) => layer?.["source-layer"] === "water");
  const coast = water ? [{
    id: "satellite_coast",
    type: "line",
    source: water.source,
    "source-layer": "water",
    filter: ["==", ["get", "class"], "ocean"],
    layout: { "line-join": "round" },
    paint: { ...SATELLITE_COAST_PAINT },
  }] : [];
  const layers = [...coast, ...borders];
  return {
    version: 8,
    name: "RadarRewind satellite borders",
    sources: source.sources || {},
    layers,
  };
}
